import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  DEFAULT_SHARE_IMAGE,
  ORGANISATION,
  SITE_NAME,
  SITE_URL,
  getSeoForPath,
} from "./seoConfig";

/**
 * Emits the metadata for whichever route is currently rendered.
 *
 * Mounted once inside the Router; it reads the location itself, so every route
 * gets its own title, description and canonical without each page component
 * having to remember to declare them. Individual props still win when passed.
 */
function SEOComponent({ title, description, image, noindex }) {
  const { pathname } = useLocation();
  const page = getSeoForPath(pathname);

  const pageTitle = title || page.title;
  // Home already carries the brand in its title; other pages get it appended.
  const fullTitle =
    page.path === "/"
      ? `${SITE_NAME} | ${pageTitle}`
      : `${pageTitle} | ${SITE_NAME}`;

  const metaDescription = description || page.description;
  const canonical = `${SITE_URL}${page.path === "/" ? "/" : page.path}`;
  const shareImage = image || DEFAULT_SHARE_IMAGE;
  const isNoindex = noindex ?? page.noindex ?? false;

  const postalAddress = {
    "@type": "PostalAddress",
    streetAddress: ORGANISATION.streetAddress,
    addressLocality: ORGANISATION.addressLocality,
    addressRegion: ORGANISATION.addressRegion,
    addressCountry: ORGANISATION.addressCountry,
    // Only emitted once a real value is filled in; an empty or guessed
    // postcode is worse than none for local matching.
    ...(ORGANISATION.postalCode
      ? { postalCode: ORGANISATION.postalCode }
      : {}),
  };

  /**
   * ProfessionalService is a subtype of both Organization and LocalBusiness,
   * so one node covers the company identity *and* the local-business signals
   * (address, phone, area served) that regional queries such as
   * "CAD automation company in Bangalore" depend on. The previous markup used
   * invented types ("TechnologyCompany", "EngineeringTrainingHub") that are not
   * in the schema.org vocabulary, which invalidated the whole block.
   */
  const organisationSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${SITE_URL}/#organization`,
    name: SITE_NAME,
    legalName: ORGANISATION.legalName,
    url: `${SITE_URL}/`,
    logo: `${SITE_URL}/HT_LOGO_1.png`,
    image: `${SITE_URL}/HT_LOGO_1.png`,
    email: ORGANISATION.email,
    telephone: ORGANISATION.telephone,
    foundingDate: ORGANISATION.foundingDate,
    address: postalAddress,
    areaServed: { "@type": "Country", name: "India" },
    knowsAbout: [
      "CAD Automation",
      "CAD Customization",
      "CATIA",
      "Siemens NX",
      "PTC CREO",
      "Product Lifecycle Management",
      "PTC Windchill",
      "Siemens Teamcenter",
      "3DEXPERIENCE",
      "Automotive Design Engineering",
      "Computer Vision",
      "Machine Learning",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      telephone: ORGANISATION.telephone,
      email: ORGANISATION.email,
      contactType: "sales",
      areaServed: "IN",
      availableLanguage: ["en"],
    },
    sameAs: ORGANISATION.sameAs,
  };

  const serviceSchema = page.service && {
    "@context": "https://schema.org",
    "@type": "Service",
    name: page.service,
    description: metaDescription,
    url: canonical,
    serviceType: page.service,
    provider: { "@id": `${SITE_URL}/#organization` },
    areaServed: { "@type": "Country", name: "India" },
  };

  // Breadcrumbs give Google the site hierarchy to display under the result
  // instead of a bare URL. Two levels is all this site has.
  const breadcrumbSchema = page.path !== "/" && {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: `${SITE_URL}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: pageTitle,
        item: canonical,
      },
    ],
  };

  // FAQPage markup is the one rich result still available to a site this size,
  // and it answers the questions buyers actually type.
  const faqSchema = page.faqs?.length && {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: page.faqs.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={canonical} />
      {isNoindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large" />
      )}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={shareImage} />
      <meta property="og:locale" content="en_IN" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={shareImage} />

      <script type="application/ld+json">
        {JSON.stringify(organisationSchema)}
      </script>
      {serviceSchema && (
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      )}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      {faqSchema && (
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      )}
    </Helmet>
  );
}

export default SEOComponent;
